import React, { useEffect, useState } from 'react';
import { Modal, Button } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import { OptionalFilter } from './interface';
import SideFiltersContent from './SideFiltersContent';
import { FiFilter } from 'react-icons/fi';
import {
  clearSideFilter,
  setModalSideFilters,
  updateSiteFilter,
} from '../../modules/SearchDatePicker/searchDatePickerSlice';
import { isEmpty } from 'lodash';
import './SideFiltersModal.less';
import ClearFiltersButton from './components/ClearFiltersButton';

interface DynamicSorting {
  name: string;
  label: string;
}

interface Props {
  optional_filters?: OptionalFilter[];
  starsGreaterThanRatingFilter?: boolean;
  pricingMaxLimitAndPlusFilter?: number | undefined;
  handleOnClear?: () => void;
  isLoading?: boolean;
  hasPartiallyLoaded?: boolean;
  additionalPrice?: number;
  freeCancellationTooltipText?: string;
  dynamicSorting?: DynamicSorting[];
  isOpen: boolean;
  onClose: () => void;
  filterEntityType?: 'dynamic_flights' | 'bundle_flights';
  isReturnFlight?: boolean;
  showPricePerPax?: boolean;
}

const SideFiltersModal = ({
  optional_filters,
  starsGreaterThanRatingFilter,
  pricingMaxLimitAndPlusFilter,
  handleOnClear,
  isLoading,
  hasPartiallyLoaded,
  additionalPrice = 0,
  freeCancellationTooltipText,
  dynamicSorting,
  isOpen,
  onClose,
  filterEntityType,
  isReturnFlight,
  showPricePerPax,
}: Props) => {
  const { general: general_t } = useSelector(
    (state: RootState) => state.config.translations
  );
  const dispatch = useDispatch();

  const { sideFilters: reduxSideFilters, modalSideFilters } = useSelector(
    (state: RootState) => state.search
  );

  // Local state for temporary filter changes
  const [hasChanges, setHasChanges] = useState(false);

  // Initialize local filters when modal opens
  useEffect(() => {
    if (isOpen) {
      dispatch(setModalSideFilters({ ...reduxSideFilters }));
      setHasChanges(false);
    }
  }, [dispatch, isOpen, reduxSideFilters]);

  // Track changes in local filters
  useEffect(() => {
    if (isOpen) {
      const hasModifications =
        JSON.stringify(modalSideFilters) !== JSON.stringify(reduxSideFilters);
      setHasChanges(hasModifications);
    }
  }, [modalSideFilters, reduxSideFilters, isOpen]);

  const handleApplyFilters = () => {
    // Apply all local filter changes to redux
    Object.keys(modalSideFilters).forEach((key) => {
      if (modalSideFilters[key] !== reduxSideFilters[key]) {
        dispatch(updateSiteFilter({ key, value: modalSideFilters[key] }));
      }
    });

    // Close modal
    onClose();
  };

  const handleCancel = () => {
    // Revert local changes
    dispatch(setModalSideFilters({ ...reduxSideFilters }));
    onClose();
  };

  if (!optional_filters || isEmpty(optional_filters)) {
    return null;
  }

  return (
    <Modal
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <FiFilter />
          {general_t.filters}
        </div>
      }
      open={isOpen}
      onCancel={handleCancel}
      centered
      width={600}
      footer={[
        <ClearFiltersButton
          key="clear"
          optional_filters={optional_filters}
          handleOnClear={() => {
            handleOnClear && handleOnClear();
            onClose();
          }}
          withModalScopeParams
          inactiveMode="disabled"
          filterEntityType={filterEntityType}
          isReturnFlight={isReturnFlight}
        />,
        <Button
          key="apply"
          type="primary"
          className="btn-style-5"
          onClick={handleApplyFilters}
          disabled={!hasChanges}
        >
          {general_t.search}
        </Button>,
      ]}
      className="side-filters-modal"
    >
      <div className="side-filters-modal-content">
        <SideFiltersContent
          optional_filters={optional_filters}
          starsGreaterThanRatingFilter={starsGreaterThanRatingFilter}
          pricingMaxLimitAndPlusFilter={pricingMaxLimitAndPlusFilter}
          withModalScopeParams
          isLoading={isLoading}
          hasPartiallyLoaded={hasPartiallyLoaded}
          additionalPrice={additionalPrice}
          freeCancellationTooltipText={freeCancellationTooltipText}
          dynamicSorting={dynamicSorting}
          filterEntityType={filterEntityType}
          isReturnFlight={isReturnFlight}
          showPricePerPax={showPricePerPax}
        />
      </div>
    </Modal>
  );
};

export default SideFiltersModal;
